import { FunctionComponent, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { ConfigContext } from '../../../../context';
import {
  Check,
  cookieOptions,
  getCssVar,
  ORG_DASH_USERS,
  Popup,
  useIsNarrowViewport,
  useUsersTable,
} from '../../../../shared';

import classes from './UsersColumns.module.scss';

export enum UsersTableColumn {
  NAME = 'NAME',
  EMAIL = 'EMAIL',
  ID = 'ID',
  GENDER = 'GENDER',
  POSITION = 'POSITION',
  DEPARTMENT = 'DEPARTMENT',
  SUBSCRIPTION = 'SUBSCRIPTION',
  LOCATION = 'LOCATION',
  USER_TAGS = 'USER_TAGS',
  CHANNELS_COUNT = 'CHANNELS_COUNT',
  LAST_ACTIVE = 'LAST_ACTIVE',
  INVITED = 'INVITED',
  REFERRAL_COUNT = 'REFERRAL_COUNT',
  REFERRED_BY_USER = 'REFERRED_BY_USER',
  // COMMENTS_COUNT = 'COMMENTS_COUNT',
  // REACTIONS_COUNT = 'REACTIONS_COUNT',
  // LIKES_ON_COMMENTS_COUNT = 'LIKES_ON_COMMENTS_COUNT',
  // SHARES_COUNT = 'SHARES_COUNT',
  // POSTS_COUNT = 'POSTS_COUNT',
}

export const UsersColumns: FunctionComponent = () => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsers' });

  const { excludeColumns } = useContext(ConfigContext).config.elements.orgDashboard.users;

  const { getTableColumnLabel } = useUsersTable();

  const isNarrowViewport = useIsNarrowViewport();

  const [cookies, setCookie] = useCookies([ORG_DASH_USERS]);

  const [columnsOpen, setColumnsOpen] = useState<boolean>(false);

  const orgDashUsersCookies = useMemo(() => {
    return { ...cookies[ORG_DASH_USERS] };
  }, [cookies]);

  const activeColumnsDefault = useMemo(
    () => [
      UsersTableColumn.NAME,
      UsersTableColumn.EMAIL,
      UsersTableColumn.LAST_ACTIVE,
      UsersTableColumn.INVITED,
    ],
    []
  );

  const { activeColumns = activeColumnsDefault } = orgDashUsersCookies;

  const columnsBody = useMemo(() => {
    return (
      <div className={classes['columns']}>
        {Object.values(UsersTableColumn)
          .filter((value) => !new Set([...activeColumnsDefault, ...excludeColumns]).has(value))
          .map((value) => {
            return (
              <Check
                key={value}
                label={getTableColumnLabel(value)}
                checked={activeColumns.includes(value)}
                onChange={(checked) => {
                  setCookie(
                    ORG_DASH_USERS,
                    {
                      ...orgDashUsersCookies,
                      activeColumns: checked
                        ? [...new Set([...activeColumns, value])]
                        : activeColumns.filter((column: UsersTableColumn) => column !== value),
                    },
                    cookieOptions()
                  );
                }}
              />
            );
          })}
      </div>
    );
  }, [
    activeColumns,
    activeColumnsDefault,
    excludeColumns,
    getTableColumnLabel,
    orgDashUsersCookies,
    setCookie,
  ]);

  return (
    <Popup
      isOpen={columnsOpen}
      setIsOpen={setColumnsOpen}
      iconId={'columns'}
      iconSize={18}
      label={ctxT('columns')}
      bodyTop={'1.5rem'}
      {...(isNarrowViewport && { bodyLeft: '0' })}
      {...(!isNarrowViewport && { bodyRight: '0' })}
      body={columnsBody}
      color={getCssVar('--base-link-text-color')}
      hoverColor={getCssVar('--base-link-text-hover-color')}
    />
  );
};
